import React from 'react';
import { Link } from 'react-router-dom';
import { Translate } from 'core/translate';
import { SvgIcon } from 'core/icons';
import { combineClassNames } from 'core/css';
import { NavButton, GlobalNavButton } from 'core/nav';
import { computeModelSelectorUrl } from './modelSelectorUrl';
import BreakpointMatch from '../../common/mediaQuery/BreakpointMatch';
import BrandLogo from './BrandLogo';

const MobileNavButton = ({ className, ...restProps }) => (
  <NavButton
    className={combineClassNames('global-nav__menu-item', className)}
    {...restProps}
  />
);

const MoreDropDownMenu = props => {
  const {
    onEditLocation,
    modelSelectorUrl,
    launchChat,
    useLivePersonChat,
    postalCode,
    disableSwitchModel,
    moreMenuDropdownRef,
    isMoreMenuVisible,
    closeMoreMenuDropdown,
    showMessageUs
  } = props;

  const onChatClick = () => {
    closeMoreMenuDropdown();
    launchChat(useLivePersonChat);
  };

  const onEditLocationClick = () => {
    closeMoreMenuDropdown();
    onEditLocation();
  };

  return (
    <div
      ref={moreMenuDropdownRef}
      className={`global-nav__menu hide-for-large ${
        isMoreMenuVisible
          ? 'global-nav__menu--expanded'
          : 'global-nav__menu--collapsed'
      }`}>
      <div className="global-nav__menu-inner">
        <MobileNavButton
          className="global-nav__menu-item--location"
          icon="pencil"
          text={
            <React.Fragment>
              <Translate value="component.global.nav.edit.location" />
              <span className="global-nav__postal-code">{postalCode}</span>
            </React.Fragment>
          }
          onClick={onEditLocationClick}
        />
        <MobileNavButton
          as="a"
          href={modelSelectorUrl}
          enable={!disableSwitchModel}
          icon="exchange"
          text={<Translate value="switch.model" />}
        />
        <MobileNavButton
          as={Link}
          to="/summary/share"
          icon="heart"
          text={<Translate value="component.global.nav.saved" />}
          onClick={closeMoreMenuDropdown}
        />
        {showMessageUs && (
          <MobileNavButton
            enable={useLivePersonChat}
            onClick={onChatClick}
            icon="chat"
            text={<Translate value="chat" />}
          />
        )}
      </div>
    </div>
  );
};

const GlobalNav = props => {
  const {
    modelInfo,
    params,
    disablePostalCode,
    onEditLocation,
    disableSwitchModel,
    shoppingLinks,
    onToggleMore,
    isMoreMenuVisible,
    moreMenuDropdownRef,
    forwardedRef,
    useLivePersonChat,
    closeMoreMenuDropdown
  } = props;

  const modelSelectorUrl = computeModelSelectorUrl(modelInfo)(shoppingLinks);
  const postalCode = params.postalCode;
  const { isFleet, country, liveChatType } = modelInfo;
  const showMessageUs = !isFleet || (isFleet && country !== 'US');

  const launchChat = ( useLivePersonChat ) => {
    if (useLivePersonChat) {
      if (liveChatType === 'salesforce') {
        window.SFM_LaunchChat();
      } else {
        const liveChatNode = document.getElementById('Build_Your_Own_Button')
          .childNodes[0];
        if (liveChatNode) {
          liveChatNode.click();
        }
        return false;
      }
    }
  };

  return (
    <div className="global-nav" ref={forwardedRef}>
      <div className="global-nav__bar">
        <BrandLogo modelInfo={modelInfo} />
        <div className="global-nav__options">
          <BreakpointMatch breakpoint="large">
            {isLarge =>
              isLarge ? (
                <React.Fragment>
                  <GlobalNavButton
                    enable={!disablePostalCode}
                    className="global-nav__option--location"
                    icon="pencil"
                    text={
                      <React.Fragment>
                        <Translate value="component.global.nav.edit.location" />
                        <span className="global-nav__postal-code">
                          {postalCode}
                        </span>
                      </React.Fragment>
                    }
                    onClick={onEditLocation}
                  />
                  <GlobalNavButton
                    as="a"
                    href={modelSelectorUrl}
                    target="_self"
                    icon="exchange"
                    text={<Translate value="switch.model" />}
                    enable={!disableSwitchModel}
                  />
                  <GlobalNavButton
                    as={Link}
                    to="/summary/share"
                    icon="heart"
                    text={<Translate value="component.global.nav.saved" />}
                  />
                  {showMessageUs && (
                    <GlobalNavButton
                      enable={useLivePersonChat}
                      icon="chat"
                      text={<Translate value="chat" />}
                      onClick={() => launchChat(useLivePersonChat)}
                    />
                  )}
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <GlobalNavButton
                    enable={!disablePostalCode}
                    className="global-nav__option--location"
                    onClick={onEditLocation}>
                    <div className="flex flex--align-center">
                      <span className="global-nav__postal-code">
                        {postalCode}
                      </span>
                      <SvgIcon
                        name="pencil"
                        className="global-nav__postal-code-icon--stacked"
                      />
                    </div>
                    <span className="global-nav__option-text global-nav__location-label--full">
                      <Translate value="component.global.nav.edit.location" />
                    </span>
                    <span className="global-nav__option-text global-nav__location-label--short">
                      <Translate value="edit" />
                    </span>
                  </GlobalNavButton>
                  <GlobalNavButton onClick={onToggleMore}>
                    <SvgIcon name="more" />
                    <span className="global-nav__option-text global-nav__option-text--more">
                      <Translate value="component.global.nav.more" />
                    </span>
                    <span className="global-nav__option-text global-nav__option-text--menu">
                      <Translate value="component.global.nav.menu" />
                    </span>
                  </GlobalNavButton>
                </React.Fragment>
              )
            }
          </BreakpointMatch>
        </div>
      </div>

      <MoreDropDownMenu
        onEditLocation={onEditLocation}
        modelSelectorUrl={modelSelectorUrl}
        launchChat={launchChat}
        useLivePersonChat={useLivePersonChat}
        postalCode={params.postalCode}
        disableSwitchModel={disableSwitchModel}
        moreMenuDropdownRef={moreMenuDropdownRef}
        isMoreMenuVisible={isMoreMenuVisible}
        closeMoreMenuDropdown={closeMoreMenuDropdown}
        showMessageUs={showMessageUs}
      />
    </div>
  );
};

export default GlobalNav;
